import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Flag, Lock, Loader2, LogOut, Search, Users, Zap, NotebookPen, Target, Camera, EyeOff, Activity, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, errorMessage, titleCase } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { BeltBar } from "@/components/BeltBadge";

const ADMIN_KEY = "matiq_admin_token";
const adminHeaders = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem(ADMIN_KEY)}` } });
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const STYLE = { gi: "Gi", nogi: "No-Gi", both: "Gi & No-Gi" };

function Shell({ children, onBack, right }) {
  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="max-w-md mx-auto min-h-screen bg-[#0A0A0A] px-5 pt-8 pb-16">
        <div className="flex items-center justify-between mb-6">
          <button data-testid="admin-back-button" onClick={onBack} className="-ml-3 h-11 pl-2 pr-4 rounded-full flex items-center gap-1 text-[15px] font-medium text-[#A1A1AA] hover:text-white active:bg-[#161618] transition-colors">
            <ChevronLeft size={22} /> Back
          </button>
          {right}
        </div>
        {children}
      </div>
    </div>
  );
}

function AdminLogin({ onDone }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/login", { password: pw });
      sessionStorage.setItem(ADMIN_KEY, data.token);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="pt-10 space-y-4 rise" data-testid="admin-login-form">
      <Lock className="text-[#E50914]" size={28} />
      <h1 className="font-display text-3xl font-bold uppercase tracking-tight">Admin</h1>
      <input data-testid="admin-password-input" type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" className="field" />
      {error && <div data-testid="admin-login-error" className="text-sm text-[#FF3B30]">{error}</div>}
      <button data-testid="admin-login-button" disabled={!pw || busy} className="btn-primary">{busy ? <Loader2 size={18} className="animate-spin" /> : "Unlock"}</button>
    </form>
  );
}

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="card p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 eyebrow mb-2"><Icon size={13} />{label}</div>
      <div className="font-display text-3xl font-semibold leading-none">{value}</div>
      {sub && <div className="text-xs text-[#71717A] mt-1.5">{sub}</div>}
    </div>
  );
}

function Bars({ title, rows, testid }) {
  const max = Math.max(1, ...rows.map((r) => r[1]));
  return (
    <section className="card p-5" data-testid={testid}>
      <div className="eyebrow mb-3">{title}</div>
      {rows.length === 0 && <div className="text-sm text-[#71717A]">No data yet</div>}
      <div className="space-y-2.5">
        {rows.map(([k, v]) => (
          <div key={k}>
            <div className="flex justify-between text-sm mb-1"><span className="capitalize">{STYLE[k] || k}</span><span className="text-[#A1A1AA]">{v}</span></div>
            <div className="h-1.5 rounded-full bg-[#222225]"><div className="h-full rounded-full bg-[#E50914]" style={{ width: `${(v / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SignupChart({ days }) {
  const max = Math.max(1, ...days.map((d) => d[1]));
  return (
    <section className="card p-5" data-testid="admin-signup-chart">
      <div className="eyebrow mb-4">Sign-ups · last 14 days</div>
      <div className="flex items-end gap-1.5 h-24">
        {days.map(([d, v]) => (
          <div key={d} className="flex-1 flex flex-col items-center justify-end gap-1 h-full" title={`${d}: ${v}`}>
            {v > 0 && <span className="text-[10px] text-[#A1A1AA]">{v}</span>}
            <div className="w-full rounded-sm bg-[#E50914]" style={{ height: `${Math.max(v ? 8 : 2, (v / max) * 80)}%`, opacity: v ? 1 : 0.25 }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-[#52525B] mt-2"><span>{days[0]?.[0].slice(5)}</span><span>Today</span></div>
    </section>
  );
}

const Field = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2.5 border-b border-[#1F1F23] last:border-0 text-sm">
    <span className="text-[#71717A]">{label}</span>
    <span className="text-right text-white break-all">{value ?? "—"}</span>
  </div>
);

function UserSheet({ user, onClose, onChanged }) {
  const toggleHide = async (visible) => {
    try {
      await api.patch(`/admin/users/${user.id}`, { hide_from_leaderboard: !visible }, adminHeaders());
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const togglePro = async (on) => {
    try {
      await api.patch(`/admin/users/${user.id}`, { admin_pro: on }, adminHeaders());
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/admin/users/${user.id}`, adminHeaders());
      toast.success("User deleted");
      onClose();
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <Sheet open={!!user} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="bottom" className="bg-[#0A0A0A] border-[#27272A] max-w-md mx-auto rounded-t-3xl h-[88vh] overflow-y-auto px-5 pb-10" data-testid="admin-user-sheet">
        {user && (
          <>
            <SheetHeader className="text-left mb-5">
              <div className="flex items-center gap-4">
                <Avatar id={user.id} version={user.avatar_version} name={user.display_name || user.email} className="w-14 h-14 rounded-2xl" textClass="text-2xl" />
                <div className="min-w-0">
                  <SheetTitle className="font-display text-2xl uppercase tracking-tight truncate">{user.display_name || "Not onboarded"}</SheetTitle>
                  <SheetDescription className="text-[#A1A1AA] truncate">{user.email}</SheetDescription>
                </div>
              </div>
            </SheetHeader>
            <div className="card px-4 mb-4">
              <Field label="Signed up" value={when(user.created_at)} />
              <Field label="Onboarded" value={user.onboarded ? "Yes" : "No"} />
              <Field label="Accepted terms" value={when(user.accepted_terms_at)} />
              <Field label="Last log" value={when(user.last_active)} />
              <Field label="Time zone" value={user.timezone} />
            </div>
            <div className="card px-4 mb-4">
              <Field label="Age" value={user.age} />
              <Field label="Belt" value={`${titleCase(user.belt)} · ${user.stripes} stripe${user.stripes === 1 ? "" : "s"}`} />
              <Field label="Gym" value={user.gym || "—"} />
              <Field label="Years training" value={user.years_training} />
              <Field label="Sessions / week" value={user.sessions_per_week} />
              <Field label="Style" value={STYLE[user.style]} />
              <Field label="Goal" value={user.goal || "—"} />
            </div>
            <div className="card px-4 mb-4">
              <Field label="Sessions logged" value={user.total_sessions} />
              <Field label="Training days" value={user.training_days} />
              <Field label="XP" value={`${user.xp} · Level ${user.level}`} />
              <Field label="Reminders" value={user.reminders_enabled ? "On" : "Off"} />
              <Field label="Quizzes completed" value={user.quizzes_completed} />
              <Field label="Plan" value={`${user.plan === "pro" ? "Pro" : "Free"}${user.plan_info?.source ? ` · ${user.plan_info.source.replace("_", " ")}` : ""}`} />
            </div>
            <label className="card p-4 flex items-center justify-between gap-4 mb-3 cursor-pointer">
              <span><span className="text-sm font-semibold block">Give Pro for free</span><span className="text-xs text-[#71717A]">For coaches and testers. Doesn't use a founder spot.</span></span>
              <Switch data-testid="admin-user-pro-switch" checked={!!user.admin_pro} onCheckedChange={togglePro} />
            </label>
            <label className="card p-4 flex items-center justify-between gap-4 mb-4 cursor-pointer">
              <span className="text-sm font-semibold">Show on leaderboard</span>
              <Switch data-testid="admin-user-leaderboard-switch" checked={!user.hide_from_leaderboard} onCheckedChange={toggleHide} />
            </label>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="admin-user-delete-button" className="w-full h-11 text-sm text-[#71717A] hover:text-[#FF3B30] transition-colors">Delete this user</button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#161618] border-[#27272A] max-w-[90vw] sm:max-w-sm rounded-2xl">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {user.display_name || user.email}?</AlertDialogTitle>
                  <AlertDialogDescription>Permanently removes their account, sessions and plans.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="admin-user-delete-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="admin-user-delete-confirm" onClick={remove} className="bg-[#E50914] hover:bg-[#B20710] text-white">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Reports() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("open");
  const { data = [], isLoading } = useQuery({
    queryKey: ["admin-reports", status],
    queryFn: () => api.get(`/admin/reports?status=${status}`, adminHeaders()).then((r) => r.data),
  });
  const setReport = async (id, next) => {
    try {
      await api.patch(`/admin/reports/${id}`, { status: next }, adminHeaders());
      qc.invalidateQueries({ queryKey: ["admin-reports"] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <section data-testid="admin-reports">
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow flex items-center gap-1.5"><Flag size={12} /> Question reports</div>
        <div className="flex gap-1 p-0.5 bg-[#161618] border border-[#27272A] rounded-lg">
          {["open", "resolved"].map((s) => (
            <button key={s} data-testid={`admin-reports-tab-${s}`} onClick={() => setStatus(s)} className={`h-8 px-3 rounded-md text-xs font-semibold capitalize ${status === s ? "bg-[#E50914] text-white" : "text-[#A1A1AA]"}`}>{s}</button>
          ))}
        </div>
      </div>
      {isLoading && <div className="card h-20 animate-pulse" />}
      {!isLoading && data.length === 0 && <div className="card p-5 text-sm text-[#71717A] text-center" data-testid="admin-reports-empty">No {status} reports</div>}
      <div className="space-y-2.5">
        {data.map((r) => (
          <div key={r.id} className="card p-4" data-testid={`admin-report-${r.id}`}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#F59E0B]">{r.reason_label}</span>
              <span className="text-[11px] text-[#71717A]">{when(r.created_at)}</span>
            </div>
            <p className="text-sm font-semibold leading-snug">{r.question}</p>
            <p className="text-xs text-[#6EE7B7] mt-1.5"><span className="text-[#71717A]">Marked correct: </span>{r.correct_answer}</p>
            {r.note && <p className="text-sm text-[#D4D4D8] mt-2 bg-[#111113] border border-[#27272A] rounded-lg p-2.5">"{r.note}"</p>}
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-[#71717A] truncate">{r.display_name || r.email} · {r.kind} · {r.qid}</span>
              <button data-testid={`admin-report-toggle-${r.id}`} onClick={() => setReport(r.id, status === "open" ? "resolved" : "open")} className="h-9 px-3 rounded-lg border border-[#27272A] text-xs font-semibold hover:border-[#E50914] transition-colors shrink-0">
                {status === "open" ? "Mark resolved" : "Reopen"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Dashboard({ onLogout }) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-overview"],
    queryFn: () => api.get("/admin/overview", adminHeaders()).then((r) => r.data),
    retry: false,
  });
  const unauthorized = error?.response?.status === 401;
  useEffect(() => {
    if (unauthorized) onLogout();
  }, [unauthorized]); // eslint-disable-line react-hooks/exhaustive-deps

  const users = useMemo(() => {
    const s = q.trim().toLowerCase();
    const all = data?.users || [];
    return s ? all.filter((u) => `${u.email} ${u.display_name} ${u.gym}`.toLowerCase().includes(s)) : all;
  }, [data, q]);
  const open = data?.users.find((u) => u.id === openId) || null;

  if (isLoading || !data) return <div className="card h-64 animate-pulse" />;
  const t = data.totals;

  return (
    <div className="space-y-5" data-testid="admin-dashboard">
      <h1 className="font-display text-3xl font-bold uppercase tracking-tight">Admin</h1>
      <div className="grid grid-cols-2 gap-3">
        <Stat icon={Users} label="Users" value={t.users} sub={`${t.onboarded} finished onboarding`} testid="admin-stat-users" />
        <Stat icon={UserPlus} label="New (7d)" value={t.signups_7d} sub={`${t.signups_24h} today · ${t.signups_30d} in 30d`} testid="admin-stat-signups" />
        <Stat icon={Activity} label="Active (7d)" value={t.active_7d} sub="logged a session" testid="admin-stat-active" />
        <Stat icon={Zap} label="Earned XP" value={t.with_xp} sub={`${t.hidden} hidden from ranks`} testid="admin-stat-xp" />
        <Stat icon={NotebookPen} label="Sessions" value={t.sessions} sub={`${t.sessions_7d} this week`} testid="admin-stat-sessions" />
        <Stat icon={Target} label="Quizzes done" value={t.quizzes} sub={`${t.open_reports} open question report${t.open_reports === 1 ? "" : "s"}`} testid="admin-stat-quizzes" />
      </div>
      <Reports />
      <SignupChart days={data.signups_by_day} />
      <Bars title="Belts" rows={data.belts} testid="admin-belts" />
      <Bars title="Goals" rows={data.goals} testid="admin-goals" />
      <Bars title="Gi / No-Gi" rows={data.styles} testid="admin-styles" />

      <section>
        <div className="eyebrow mb-3">All users · newest first</div>
        <div className="relative mb-3">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email, name, gym" className="field pl-11" />
        </div>
        <div className="card divide-y divide-[#1F1F23] overflow-hidden" data-testid="admin-user-list">
          {users.map((u) => (
            <button key={u.id} data-testid={`admin-user-${u.id}`} onClick={() => setOpenId(u.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#161618] transition-colors">
              <Avatar id={u.id} version={u.avatar_version} name={u.display_name || u.email} className="w-10 h-10 rounded-full" textClass="text-base" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate flex items-center gap-1.5">
                  {u.display_name || <span className="text-[#71717A]">Not onboarded</span>}
                  {u.hide_from_leaderboard && <EyeOff size={12} className="text-[#71717A]" />}
                  {u.plan === "pro" && <span className="text-[9px] font-bold uppercase bg-[#E50914] rounded px-1">Pro</span>}
                  {u.avatar_version && <Camera size={12} className="text-[#71717A]" />}
                </div>
                <div className="text-xs text-[#71717A] truncate">{u.email}</div>
              </div>
              <div className="text-right shrink-0">
                {u.onboarded && <BeltBar belt={u.belt} stripes={u.stripes} className="w-12 h-2.5 ml-auto" />}
                <div className="text-[11px] text-[#71717A] mt-1">{u.total_sessions} logs · {new Date(u.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</div>
              </div>
            </button>
          ))}
          {users.length === 0 && <div className="p-6 text-sm text-[#71717A] text-center">No users found</div>}
        </div>
      </section>
      <UserSheet user={open} onClose={() => setOpenId(null)} onChanged={() => qc.invalidateQueries({ queryKey: ["admin-overview"] })} />
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(!!sessionStorage.getItem(ADMIN_KEY));
  const logout = () => {
    sessionStorage.removeItem(ADMIN_KEY);
    setAuthed(false);
  };
  return (
    <Shell
      onBack={() => navigate("/")}
      right={authed && <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white transition-colors"><LogOut size={15} /> Lock</button>}
    >
      {authed ? <Dashboard onLogout={logout} /> : <AdminLogin onDone={() => setAuthed(true)} />}
    </Shell>
  );
}
